import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  MODAL_CONTENT_SX,
  MODAL_HEADER_SX,
  MODAL_FOOTER_SX,
} from "../../../components/Portal/portalModalChrome";

const TEXT_SX = { marginBottom: "0 !important" };

/**
 * Confirms closing a mission and lets the company decide whether the direct
 * applicants get the automatic "position filled" email. `onConfirm` receives
 * `{ notifyApplicants }`; with no applicants there is nothing to ask.
 */
const CloseMissionDialog = ({ isOpen, onClose, onConfirm, applicantCount = 0, isLoading = false }) => {
  const { t } = useTranslation("portal");
  const hasApplicants = applicantCount > 0;
  const [pendingChoice, setPendingChoice] = useState(null);

  useEffect(() => {
    if (!isLoading) setPendingChoice(null);
  }, [isLoading]);

  const confirm = (notifyApplicants) => {
    setPendingChoice(notifyApplicants);
    onConfirm({ notifyApplicants });
  };

  return (
    <Modal isOpen={isOpen} onClose={isLoading ? () => {} : onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent sx={MODAL_CONTENT_SX} data-testid="close-mission-dialog">
        <ModalHeader sx={MODAL_HEADER_SX}>
          {t("partners.missions.closeDialog.title", "Close this mission?")}
        </ModalHeader>
        <ModalCloseButton isDisabled={isLoading} />
        <ModalBody py="18px">
          <Text fontSize="sm" color="portal.muted" sx={TEXT_SX}>
            {t(
              "partners.missions.closeDialog.body",
              "The mission will be closed and will no longer accept applications."
            )}
          </Text>
          {hasApplicants ? (
            <Box
              mt="16px"
              p="14px"
              borderRadius="10px"
              border="1px solid"
              borderColor="portal.line"
              bg="#F8FBFD"
            >
              <Text fontSize="sm" fontWeight={700} color="portal.text" sx={TEXT_SX}>
                {t(
                  "partners.missions.closeDialog.notifyQuestion",
                  "Would you like to send an automatic notification to all applicants?"
                )}
              </Text>
              <Text fontSize="sm" color="portal.muted" mt="6px" sx={TEXT_SX}>
                {t("partners.missions.closeDialog.notifyHint", {
                  count: applicantCount,
                  defaultValue:
                    "The {{count}} applicant(s) to this mission will receive an email informing them that the position has been filled.",
                })}
              </Text>
              <Text fontSize="xs" color="portal.muted" mt="6px" sx={TEXT_SX}>
                {t(
                  "partners.missions.closeDialog.notifyExclusions",
                  "The positioned candidate and candidates submitted by partner companies are not notified."
                )}
              </Text>
            </Box>
          ) : (
            <Text fontSize="sm" color="portal.muted" mt="12px" sx={TEXT_SX}>
              {t(
                "partners.missions.closeDialog.noApplicants",
                "No one has applied to this mission, so no notification will be sent."
              )}
            </Text>
          )}
        </ModalBody>
        <ModalFooter sx={MODAL_FOOTER_SX}>
          <Button size="sm" variant="secondary" onClick={onClose} isDisabled={isLoading}>
            {t("partners.missions.closeDialog.cancel", "Cancel")}
          </Button>
          {hasApplicants ? (
            <>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => confirm(false)}
                isLoading={isLoading && pendingChoice === false}
                isDisabled={isLoading && pendingChoice !== false}
                data-testid="close-mission-no-notify"
              >
                {t("partners.missions.closeDialog.no", "No, close without notifying")}
              </Button>
              <Button
                size="sm"
                variant="primary"
                onClick={() => confirm(true)}
                isLoading={isLoading && pendingChoice === true}
                isDisabled={isLoading && pendingChoice !== true}
                data-testid="close-mission-notify"
              >
                {t("partners.missions.closeDialog.yes", "Yes, notify applicants")}
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              colorScheme="red"
              onClick={() => confirm(false)}
              isLoading={isLoading}
              data-testid="close-mission-confirm"
            >
              {t("partners.missions.detail.closeMission", "Close Mission")}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CloseMissionDialog;
